/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/dread-hf.ts
   Dread domain cards, Hope & Fear (final) version. The Void playtest
   version lives in dread.tsx. `rendered` is generated from `text` by
   parseCardTextWithTerms, so edit `text` only.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import { parseCardTextWithTerms } from '../../parseText';

const cards: Omit<DomainCard, 'rendered'>[] = [
  {
    name: "Blighting Strike",
    level: 1,
    domain: "Dread",
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Far range. On a success:\n\n- On a roll with Hope, deal **d6+1** magic damage using your Proficiency. On a roll with Fear, deal **d10+1** magic damage using your Proficiency.\n- The target's next successful attack deals half damage.\n\nOn a failure, you must spend a Hope or mark a Stress.",
  },
  {
    name: "Umbral Veil",
    level: 1,
    domain: "Dread",
    type: "Spell",
    recallCost: 1,
    text: "Once per rest, you can **mark a Stress** to encase yourself in shadowy energy. When you do, place a number of tokens on this card equal to the number of Fear in the GM's pool. After an attack roll is made against you, you can spend any number of tokens to give the result a -1 penalty per token spent.\n\nAt the end of the scene, clear all unspent tokens.",
  },
  {
    name: "Voice of Dread",
    level: 1,
    domain: "Dread",
    type: "Spell",
    recallCost: 0,
    text: "You can magically speak to a creature you can see, tormenting them with your words. Make a Spellcast Roll against them. On a success, they must mark a Stress and are frozen with terror, making them temporarily *Restrained*.",
  },
  {
    name: "Hideous Retribution",
    level: 2,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "When an ally within Close range takes damage from a target you can see, you can make a reaction roll against the target using your Spellcast trait. On a success, **mark a Stress** to deal **d6** magic damage using your Proficiency.",
  },
  {
    name: "Siphon Essence",
    level: 2,
    domain: "Dread",
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Very Close range. Once per long rest on a success, the target takes **d12+4** magic damage using your Proficiency. On a success with Fear, you gain a +1 bonus to your Proficiency for this attack.\n\nYou clear a number of Hit Points equal to the number of Hit Points the target marked from this attack.",
  },
  {
    name: "Shared Trauma",
    level: 3,
    domain: "Dread",
    type: "Spell",
    recallCost: 1,
    text: "You can transfer suffering from one creature to another. Once per rest, mark any number of Hit Points on a willing creature within Melee range to clear an equal number of Hit Points on another willing creature within Melee range.",
  },
  {
    name: "Terrify",
    level: 3,
    domain: "Dread",
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Close range. On a success, the target marks **1d4** Stress, and you can make the target flee one range away from you (such as Very Close to Close or Close to Far). On a success with Fear, the target also becomes temporarily *Vulnerable*.",
  },
  {
    name: "Chains of Affliction",
    level: 4,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "**Mark 2 Stress** to temporarily *Chain* a target within Close range. When a *Chained* creature deals damage, the target of their attack marks one fewer Hit Point than they normally would. You can't have more than one creature *Chained* at a time.",
  },
  {
    name: "Summon Horror",
    level: 4,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "Once per scene, **mark a Stress** to summon an otherworldly creature that deals **d8+1** magic damage using your Spellcast trait to a target within Far range. If the target marks any Hit Points from this attack, they must succeed on a Reaction Roll (12) to steel themselves against the horror or mark an equal number of Stress.\n\nAfter making the attack, the creature dissipates.",
  },
  {
    name: "Dire Strike",
    level: 5,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "When a target marks any number of Hit Points from an attack you make, you can **spend a Hope** to drain power from them. The GM loses a Fear.",
  },
  {
    name: "Spectral Mist",
    level: 5,
    domain: "Dread",
    type: "Spell",
    recallCost: 0,
    text: "**Spend 2 Hope** to conjure an eerie mist that turns you and allies of your choice within Close range momentarily incorporeal. While a creature is incorporeal, they can move through solid objects and are immune to physical damage. They become corporeal again after they pass through a solid object or make an action roll. Otherwise, this effect lasts until the end of the scene.",
  },
  {
    name: "Darkfire",
    level: 6,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "Once per scene, **spend any number of Hope** to target an equal number of adversaries within Close range. Each target makes a Reaction Roll (15). Targets who fail take **d8+6** magic damage using your Spellcast trait as they are engulfed in dark fire. Targets who succeed take half damage.",
  },
  {
    name: "Jump Scare",
    level: 6,
    domain: "Dread",
    type: "Spell",
    recallCost: 1,
    text: "When you deal magic damage to a target, you can **mark a Stress** to teleport into Melee range with them. When you do, they are *Vulnerable* until they mark 1 or more Hit Points.",
  },
  {
    name: "Dread-Touched",
    level: 7,
    domain: "Dread",
    type: "Ability",
    recallCost: 2,
    text: "When 4 or more of the domain cards in your loadout are from the Dread domain, gain the following benefits:\n\n- When you succeed with Fear, you can **mark 2 Stress** to prevent the GM from gaining a Fear.\n- Once per rest when making an action roll, you can gain a bonus to the roll equal to the number of Fear in the GM's pool.",
  },
  {
    name: "Wall of Hunger",
    level: 7,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "Make a Spellcast Roll (10). On a success, you can **spend a Hope** to create a visible wall of writhing necrotic energy between two points within Far range. The wall lasts until you mark a Hit Point or cast this spell again. A creature inside the wall when it appears or that passes through it must mark 2 Stress.",
  },
  {
    name: "Dark Army",
    level: 8,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "Make a Spellcast Roll (14). Once per long rest on a success, you can summon fiends that surround and move with you. Place 8 tokens on this card. When you deal damage to a target within Very Close range, you can spend any number of tokens to add **1d8** for each token spent to your damage roll. Additionally, when you take damage, you can spend any number of tokens to reduce the damage by **1d8** for each token spent. Each time you spend a token, a fiend acts on your behalf, then disappears.\n\nWhen you take a rest, clear all unspent tokens.",
  },
  {
    name: "Eldritch Flesh",
    level: 8,
    domain: "Dread",
    type: "Spell",
    recallCost: 1,
    text: "Gain a +1 bonus to your damage thresholds for each Stress you have marked.\n\nAdditionally, when you roll with Fear, you can **spend 2 Hope** to clear an Armor Slot.",
  },
  {
    name: "Damnation",
    level: 9,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "Make a Spellcast Roll against a target within Far range. On a success, **mark any number of Stress** to roll an equal number of **d20s**, dealing magic damage equal to the total result. If this attack defeats the target, all adversaries within Far range of the target must mark a Stress.",
  },
  {
    name: "Savor the Anguish",
    level: 9,
    domain: "Dread",
    type: "Ability",
    recallCost: 1,
    text: "When an adversary within Close range takes Severe damage, you can clear a Stress.",
  },
  {
    name: "Avatar of Terror",
    level: 10,
    domain: "Dread",
    type: "Spell",
    recallCost: 2,
    text: "**Mark a Stress** to transform into a creature fueled by fear. While in this form, you gain a **1d6** bonus to your damage rolls for each Fear in the GM's pool. Additionally, you gain a Hope when the GM spends a Fear to spotlight an adversary within Very Close range.\n\nBefore you make an action roll, you must spend a Hope. If you can't, you revert to your normal form.",
  },
  {
    name: "Invoke Torment",
    level: 10,
    domain: "Dread",
    type: "Ability",
    recallCost: 2,
    text: "You deal double damage to targets that have all their Stress marked.\n\nAdditionally, when an adversary within Close range is defeated with all its Stress marked, you gain a Hope.",
  },
];

const dreadHfCards: DomainCard[] = cards.map((card) => ({ ...card, source: 'HF', rendered: parseCardTextWithTerms(card.text) }));

export default dreadHfCards;
